/**
 * Single source of truth for the auto-instrumentation install snippets.
 *
 * Shown in:
 *   - ProviderQuickstart drawer ("Auto-instrument" tab)
 *   - Future onboarding email
 *   - Future docs page
 *
 * Keep these strings in sync with the per-package READMEs.
 */

import type { AIProvider } from '@aicostguard-dev/shared';

/** Per-package install command. */
export const INSTALL_COMMANDS = {
  python: 'pip install aicostguard-dev',
  node: 'npm install @aicostguard-dev/auto',
} as const;

/** One-line import that triggers patching. */
export const IMPORT_SNIPPETS = {
  python: 'import aicostguard.auto  # first line of your app entry file',
  node: "import '@aicostguard-dev/auto';  // first line of your app entry file",
} as const;

/** Env vars required to activate the package. */
export const ENV_SNIPPET = (apiBaseUrl: string): string =>
  `export AICG_KEY=aicg_xxxxxxxxxxxxxxxxxxxx
export AICG_URL=${apiBaseUrl}`;

/**
 * Per-provider quick context — used as a "what gets tracked" hint.
 *
 * Tier A providers have their own native instrument. Tier B providers are
 * captured automatically via the OpenAI-compatibility detection in the
 * OpenAI instrument (they all use the OpenAI SDK with a custom base URL).
 */
export const PROVIDER_AUTOINSTRUMENT_NOTE: Record<AIProvider, string> = {
  // Tier A
  openai:
    'Every `client.chat.completions.create(...)` call — sync, async, and streaming with `stream_options.include_usage`.',
  anthropic:
    'Every `client.messages.create(...)` call — sync, async, and streaming.',
  gemini:
    'Every `model.generate_content(...)` call (Google `google-generativeai` SDK).',
  cohere:
    'Every `client.chat(...)` call (Cohere ClientV2 SDK).',
  mistral:
    'Every `client.chat.complete(...)` call (Mistral SDK).',
  // Tier B — caught via OpenAI-compatibility detection
  xai:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.x.ai/v1` — no extra integration needed.',
  groq:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.groq.com/openai/v1`.',
  together:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.together.xyz/v1`.',
  fireworks:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.fireworks.ai/inference/v1`.',
  perplexity:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.perplexity.ai`.',
  deepseek:
    'Auto-detected via the OpenAI SDK with `base_url=https://api.deepseek.com`.',
  openrouter:
    'Auto-detected via the OpenAI SDK with `base_url=https://openrouter.ai/api/v1`.',
  'vercel-ai-gateway':
    'Auto-detected via Vercel AI Gateway endpoints — multi-provider routing.',
  local:
    'For self-hosted models, use Manual POST with `custom_pricing` to attribute compute cost.',
};

/**
 * Whether auto-instrumentation is shipped for this provider in v0.2.
 *
 * All Tier A and Tier B providers are now supported. Local LLMs still use
 * Manual POST because they don't share a single canonical SDK shape.
 */
export const AUTOINSTRUMENT_SUPPORTED: Record<AIProvider, boolean> = {
  // Tier A native instruments
  openai: true,
  anthropic: true,
  gemini: true,
  cohere: true,
  mistral: true,
  // Tier B OpenAI-compatible — same `import aicostguard.auto` covers all of
  // these once they're routed through the OpenAI SDK.
  xai: true,
  groq: true,
  together: true,
  fireworks: true,
  perplexity: true,
  deepseek: true,
  openrouter: true,
  'vercel-ai-gateway': true,
  // Self-hosted catch-all
  local: false,
};
